import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';
import { Item, isValidType } from '@platform/template';
import { linkTo, getSafeRandom, formatNumber, formatAcuracy, toast, getSysFieldid } from '@platform/api';
import classNames from 'classnames';
import { timeTypes, ROW_STATUS, ERROR_SCALE, selectTypes, dateTypes, switchTypes, presetColumns } from '../../utils';
import { Tooltip } from '@platform/base';

const browseEditTypes = new Set(['attachment', 'checkbox_switch', 'switch', 'switch_browse']);

export default class Cell extends Component {
    constructor(props) {
        super(props);
        let { store, rowKey, colKey } = props;
        this.itemID = store.getInstance().itemIDManager.getID({ rowKey, colKey });
        store.getInstance().register(this.itemID, this);

        this.state = { focused: false, showTip: false };
        this.dirty = false; // 值是否修改过
    }

    render() {
        let { colKey, store, hoverContent } = this.props,
            { itemtype, attrcode, render, visible } = store.getColumnProps(colKey);

        if (isValidType(itemtype)) {
            return this.renderValidCell();
        } else if (
            hoverContent && 
            (attrcode === "opr" || attrcode === 'operate') &&
            itemtype === "customer" &&
            render && 
            visible
        ) {
            return this.renderOperationCell();
        } else {
            return this.renderCustomerCell();
        }
    }

    componentWillUnmount() {
        let { store, rowKey, colKey } = this.props,
            { 
                getActiveCell,
                getCellValue,
                getColumnProps,
            } = store,
            { onAfterEvent } = store.getInstance().getComponent('Table')?.current?.props || {},
            isActive = getActiveCell().colKey === colKey && getActiveCell().rowKey === rowKey,
            cellValue = getCellValue(rowKey, colKey),
            { itemtype, languageMeta } = getColumnProps(colKey);

        store.getInstance().unregister(this.itemID, this);
        if (isActive) {
            store.setActiveCell({ colKey: '', rowKey: '' });

            if (this.dirty) {
                if (itemtype === 'residtxt') {
                    cellValue = languageMeta.reduce((value, { index }) => {
                        if (index == 1 || index == '') {
                            value[colKey] = getCellValue(rowKey, colKey);
                        } else {
                            value[colKey + index] = getCellValue(rowKey, colKey + index);
                        }
                        return value;
                    }, {});
                }

                onAfterEvent?.({ 
                    value: cellValue, 
                    oldValue: this.oldValue, 
                    componentValue: this.oldComponentValue, 
                    event: new Event('FAKE_EVENT'), 
                    colKey, 
                    rowKey, 
                });
            }
        }
    }

    renderValidCell = () => {
        let { colKey, rowKey, store } = this.props,
            { focused } = this.state,
            { 
                getActiveCell, 
                getCellProps, 
                getColumnProps, 
                getStatus, 
                setActiveCell,
            } = store,
            columnInfo = getColumnProps(colKey),
            cellProps = getCellProps(rowKey, colKey),
            { alwaysBrowse } = store.getInstance().getComponent('Table').current.props,
            status = getStatus(),
            { itemtype } = columnInfo,
            isEditStatus = status !== 'browse' && itemtype !== 'label' && itemtype !== 'customer',
            isActive = false,
            invalid = false,
            disabled = false;
        
        // alwaysBrowse给SimpleTable加，switch_browse不显示组件，显示文字
        if (itemtype === 'attachment' || (itemtype === 'switch_browse' && !alwaysBrowse)) {
            isEditStatus = true;
        }

        if (isEditStatus) {
            isActive = getActiveCell().colKey === colKey && getActiveCell().rowKey === rowKey;
            ({ invalid, disabled } = cellProps);
        }

        return <div 
            onFocus={event => {
                if (this.mousedown) return;
                event.currentTarget.contains(event.target) && this.setState({ focused: true });

                if (disabled) return;

                if (isEditStatus && event.target === event.currentTarget) {
                    setActiveCell({ colKey, rowKey });
                    if (switchTypes.has(itemtype)) {
                        findDOMNode(this)?.querySelector('.template-item-wrapper')?.focus();
                    }
                }
            }} 
            onBlur={() => {
                this.setState({ focused: false });
                if (browseEditTypes.has(itemtype)) {
                    setActiveCell({ colKey: '', rowKey: '' });
                }
            }}
            // onClick={this.handleBeforeEvent}
            tabIndex={focused ? -1 : 0}
            className={classNames(
                'table-cell', 
                `cell-type-${itemtype}`,
                {
                    'active-cell': isActive, 
                    'disabled-cell': disabled,
                    'editable-cell': !disabled,
                    'invalid-cell': invalid,
                    'valid-type': isValidType(itemtype),
                    'focused-cell': focused,
                }, 
            )}
            onPaste={this.handlePaste}
            onMouseDown={() => {
                this.mousedown = true;
                const handleMouseUp = () => {
                    this.mousedown = false;
                    document.removeEventListener('mouseup', handleMouseUp);
                };
                document.addEventListener('mouseup', handleMouseUp);
            }}
            onMouseEnter={() => {
                let { showTip } = this.state;
                let cellDom = findDOMNode(this);
                if (cellDom) {
                    let tipNode = cellDom.querySelector('.table-cell-browse, .cell-hyper'); // 这俩类不会同时存在
                    if (tipNode) {
                        if (tipNode.scrollWidth > tipNode.clientWidth) {
                            !showTip && this.setState({ showTip: true });
                        } else {
                            showTip && this.setState({ showTip: false });
                        }
                    }
                }
            }}
            onClick={() => {
                !disabled && isEditStatus && setActiveCell({ colKey, rowKey });
            }}
        >
            {this.renderPrefixIcon(itemtype, { isEditStatus, isActive })}
            {this.renderCell({ isEditStatus, isActive })}
            {this.renderSuffixIcon(itemtype, { isEditStatus, isActive, disabled })}
        </div>;
    }

    renderCustomerCell = () => {
        let { rowKey, colKey, store } = this.props,
            { 
                getColumnProps, 
                getRowData,
                getCellValue,
            } = store,
            columnInfo = getColumnProps(colKey),
            { attrcode, render } = columnInfo;
        
        let row = getRowData(rowKey);

        if (!row) return null;

        let { dataIndex } = row;
        row = store.getInstance()._formatOutputRow(row);
        // 兼容旧的render调用方式
        
        return <div 
            className='table-cell' 
            tabIndex={presetColumns.has(attrcode) ? -1 : 0}
            onFocus={event => {
                if (event.target === event.currentTarget) {
                    let focusableElement = event.currentTarget.querySelector('input');
                    focusableElement?.focus();
                }
            }}
        >
            {render?.(getCellValue(rowKey, attrcode), row, dataIndex)}
        </div>;
    }

    renderOperationCell = () => {
        let { rowKey, colKey, store } = this.props,
            { 
                getColumnProps, 
                getRowData,
            } = store,
            columnInfo = getColumnProps(colKey),
            { render } = columnInfo;
        
        let row = getRowData(rowKey);

        if (!row) return null;

        let { dataIndex } = row;
        row = store.getInstance()._formatOutputRow(row);

        return <div 
            style={{ overflow: 'hidden' }}
            fieldid={getSysFieldid('opr')}
        >
            <div className='operation-wrapper-hover-container'>
                {render?.('', row, dataIndex)}
            </div>
        </div>;
    }

    renderCell = ({ isEditStatus, isActive }) => {
        let { colKey, rowKey, store } = this.props,
            { 
                setActiveCell, 
                getCellProps, 
                getColumnProps, 
                getRowData,
                getCellValue,
                getStatus,
                getActiveCell,
            } = store,
            { _formatOutputRow } = store.getInstance(),
            { 
                onBeforeEvent, 
                onAfterEvent,
                defaultRenderStatus,
                renderItems,
                autoRowHeight,
                onValueChange,
            } = store.getInstance().getComponent('Table').current.props,
            columnInfo = getColumnProps(colKey),
            cellProps = getCellProps(rowKey, colKey),
            { 
                itemtype, 
                hyperlinkflag, 
                attrcode, 
                render, 
                renderStatus = defaultRenderStatus, 
                renderItem, 
            } = columnInfo,
            cellValue = getCellValue(rowKey, colKey),
            row = getRowData(rowKey),
            status = getStatus(),
            editType = 'toggle',
            autoFocus = true;
        
        if (!row) return null;
        
        row = _formatOutputRow(row);

        if (Object.prototype.toString.call(cellValue).slice(8, -1) === 'Object') {
            // 有的业务直接修改row的引用设值，稍微做个容错
            cellValue = {
                _display_: cellValue.display,
                ...cellValue,
            };
        }
    
        let extraProps = {};
        switch (itemtype) {
            case 'input':
                if (autoRowHeight) {
                    extraProps.itemtype = 'textarea';
                    extraProps.rows = 1;
                    extraProps.autoHeight = true;
                }
                extraProps.isCheckChinese = true;
                extraProps.showClose = columnInfo.hasOwnProperty('showClose') ? columnInfo.showClose : true;
                extraProps.trim = true;
                break;
            case 'textarea':
                extraProps.isCheckChinese = true;
                extraProps.rows = 1;
                extraProps.trim = true;
                if (autoRowHeight) {
                    extraProps.autoHeight = true;
                }
                break;
            case 'number':
                // 先取数据精度，没有再取模板精度，默认是0
                extraProps.scale = Number(ERROR_SCALE.has(cellValue?.scale) ? ERROR_SCALE.has(columnInfo?.scale) ? 0 : columnInfo?.scale : cellValue?.scale) ?? 0;
                cellValue = {
                    ...cellValue,
                    title: cellValue?._display_,
                    _display_: formatNumber(formatAcuracy(cellValue?.value, extraProps.scale)), // 数据格式里有颜色
                };
                break;
            case 'refer':
                if (autoRowHeight) {
                    extraProps.inputType = 'textarea';
                }
                // 表格里不阻止冒泡，因为enter要跳到下一个单元格
                extraProps.stopPropagationWhenDropdownPressEnter = false;
                break;
            case 'switch_browse':
                extraProps.disabled = status === 'edit' || cellProps?.disabled;
            // eslint-disable-next-line no-fallthrough
            case 'switch':
            case 'checkbox_switch':
                editType = 'normal';
                isEditStatus && (isActive = true);
                autoFocus = false;
                break;
            case 'attachment':
                status === 'edit' && (extraProps.disableModify = true);
                editType = 'normal';
                isActive = true;
                autoFocus = false;
                break;
            case 'residtxt':
                extraProps.trim = true;
                break;
            default:
                break;
        }

        if (isEditStatus) {
            // 单元格编辑态
            if (isActive) {
                // 单元格激活态
                return <Item
                    {...columnInfo}
                    {...cellProps}
                    {...extraProps}
                    title={cellValue?.title || cellValue?._display_}
                    path={[this.itemID]}
                    browse={false}
                    editType={editType}
                    autoFocus={autoFocus}
                    onStatusChange={({ isEdit }) => {
                        // 只有当前单元格激活，才能取消激活，防止误伤
                        if (getActiveCell().rowKey === rowKey && getActiveCell().colKey === colKey && !isEdit) {
                            setActiveCell({ colKey: '', rowKey: '' });
                        }
    
                    }}
                    getFieldValue={({ attrcode }) => {
                        return store.getCellValue(rowKey, attrcode);
                    }}
                    setFieldValue = {({ attrcode, value, componentValue }) => {
                        if (this.pasting) {
                            return;
                        }

                        this.dirty = true;
                        this.oldComponentValue = componentValue;

                        store.setCellValue(rowKey, attrcode, { value });
                        onValueChange?.(rowKey, { attrcode, value });
                    }}
                    onBeforeEvent={({ value, event }) => {
                        this.oldValue = value;
                        return onBeforeEvent?.({ value, event, colKey, rowKey });
                    }}
                    onAfterEvent={({ value, oldValue, componentValue, event }) => {
                        if (this.pasting) {
                            this.pasting = false;
                        } else {
                            this.dirty = false;
                            onAfterEvent?.({ value, oldValue, componentValue, event, colKey, rowKey });
                        }
                    }}
                    renderItem={({ isEdit }) => {
                        if (isEdit) {
                            // 编辑态时的编辑态
                            if (renderStatus === 'customer') {
                                return render(cellValue, row, getRowData(rowKey).dataIndex, store, 'edit');
                            } else if (renderItem) {
                                return renderItem(cellValue, row, getRowData(rowKey).dataIndex);
                            } else if ((renderStatus === 'edit' || !renderStatus) && render) {
                                return render(cellValue, row, getRowData(rowKey).dataIndex);
                            } else {
                                // renderItem是null，但Item要undefined才走默认渲染
                                return renderItems?.[colKey] ?? undefined;
                            }
                        } 
                    }}
                    renderInTable
                />;
            } else {
                // 单元格非激活态
                if (renderStatus === 'customer' && render) {
                    let content = render(cellValue, row, getRowData(rowKey).dataIndex, store, 'editBrowse');
                    
                    if (content === undefined) {
                        return this.renderStandardBrowse(cellValue);
                    } else {
                        return this.renderTooltip(cellValue?.title || cellValue?._display_, <div 
                            className="table-cell-browse"
                        >
                            {content}
                        </div>);
                    }
                } else if (renderStatus === 'editBrowse' && render) {
                    return this.renderTooltip(cellValue?.title || cellValue?._display_, <div 
                        className="table-cell-browse"
                    >
                        {render(cellValue, row, getRowData(rowKey).dataIndex)}
                    </div>);
                } else {
                    return this.renderStandardBrowse(cellValue);
                }
            }
        } else {
            // 单元格浏览态
            if (renderStatus === 'customer' && render) {
                let content = render(cellValue, row, getRowData(rowKey).dataIndex, store, 'browse');
                    
                if (content === undefined) {
                    return this.renderStandardBrowse(cellValue);
                } else {
                    return this.renderTooltip(cellValue?.title || cellValue?._display_, <div 
                        className="table-cell-browse"
                    >
                        {content}
                    </div>);
                }
            } else if (renderStatus === 'browse' && render) {
                return this.renderTooltip(cellValue?.title || cellValue?._display_, <div 
                    className="table-cell-browse"
                >
                    {render(cellValue, row, getRowData(rowKey).dataIndex)}
                </div>);
            } else if (hyperlinkflag) {
                return this.renderTooltip(cellValue?.title || cellValue?._display_, <a
                    className='cell-hyper code-detail-link'
                    onClick={e => {
                    //超链接跳转
                        e.stopPropagation();
                        let meta = store.getMeta();
                        linkTo(meta.pageid, meta.mainId, attrcode, cellValue.value);
                    }}
                >
                    {cellValue?._display_}
                </a>);
            } else {
                return this.renderStandardBrowse(cellValue);
            }
        }
    }

    renderTooltip = (overlay = '', content) => {
        let { showTip } = this.state;

        if (showTip) {
            return <Tooltip
                placement="rightBottom"
                overlay={overlay}
                delay={1}
                inverse
                className="table-cell-tooltip"
            >
                { content }
            </Tooltip>;
        }

        return content;
    }

    renderStandardBrowse = cellValue => {
        let { colKey, store } = this.props,
            columnInfo = store.getColumnProps(colKey),
            { 
                ishighlight, 
                contentcolor,
            } = columnInfo;

        return this.renderTooltip(cellValue?.title || cellValue?._display_, <div
            className={classNames("table-cell-browse", { 'high-light': ishighlight })}
            style={{ color: contentcolor }}
        >
            {cellValue?._display_}
        </div>);
    }

    // switch类型，浏览态前面需要加图标
    renderPrefixIcon = (itemtype, { isEditStatus }) => {
        if (isEditStatus) {
            return null;
        }

        if (new Set(['checkbox_switch', 'switch', 'switch_browse']).has(itemtype)) {
            let { colKey, rowKey, store } = this.props,
                { value } = store.getCellValue(rowKey, colKey) || {};

            if (value === 'Y') {
                value = true;
            } else if (value === 'N') {
                value = false;
            }

            return <i 
                style={{
                    width: '8px',
                    height: '8px',
                    display: 'inline-block',
                    borderRadius: '4px',
                    marginRight: '6px',
                    backgroundColor: value ? 'rgba(40,202,66,1)' : 'rgba(255,95,87,1)',
                    flex: 'none',
                }}
            ></i>;
        }
    }

    // 参照和日期类型，后面要加图标
    renderSuffixIcon = (itemtype, { isEditStatus, isActive, disabled }) => {
        let { colKey, rowKey, store } = this.props;

        if (!isEditStatus || isActive || disabled) {
            return null;
        }

        // 点图标时要自动打开
        if (itemtype === 'refer') {
            return <i 
                className='iconfont icon-canzhaozuixin hover-icon'
                onClick={() => {
                    store.setCellProps(rowKey, colKey, { autoShow: true });
                    // 延迟1500ms极大部分场景都没问题，暂无更好办法
                    setTimeout(() => {
                        store.setCellProps(rowKey, colKey, { autoShow: false });
                    }, 1500);
                }}
            ></i>;
        } else if (dateTypes.has(itemtype)) {
            return <i 
                className='iconfont icon-rilizuixin hover-icon'
                onClick={() => {
                    store.setCellProps(rowKey, colKey, { autoOpenWhenFocus: true });
                    // 延迟1500ms极大部分场景都没问题，暂无更好办法
                    setTimeout(() => {
                        store.setCellProps(rowKey, colKey, { autoOpenWhenFocus: false });
                    }, 1500);
                }}
            ></i>;
        } else if (timeTypes.has(itemtype)) {
            return <i 
                className='iconfont icon-shijian hover-icon'
                onClick={() => {
                    store.setCellProps(rowKey, colKey, { autoOpenWhenFocus: true });
                    // 延迟1500ms极大部分场景都没问题，暂无更好办法
                    setTimeout(() => {
                        store.setCellProps(rowKey, colKey, { autoOpenWhenFocus: false });
                    }, 1500);
                }}
            ></i>;
        } else if (selectTypes.has(itemtype)) {
            return <i 
                className='wui-icon uf uf-arrow-down hover-icon'
                onClick={() => {
                    store.setCellProps(rowKey, colKey, { defaultOpen: true, autoFocus: true });
                    // 延迟1500ms极大部分场景都没问题，暂无更好办法
                    setTimeout(() => {
                        store.setCellProps(rowKey, colKey, { defaultOpen: false, autoFocus: false });
                    }, 1500);
                }}
            ></i>;
        }
    }

    handlePaste = e => {
        let { colKey, store } = this.props,
            { getColumnProps } = store,
            locale = store.getLocale(),
            columnInfo = getColumnProps(colKey),
            { itemtype } = columnInfo;
        
        let pasteText = (e.clipboardData || window.clipboardData).getData('text'),
            pasteTexts = [];

        if (!/\n/.test(pasteText)) {
            // 单行时不走粘贴逻辑
            return;
        } else if (/\t/.test(pasteText)) {
            // 多列时不走粘贴逻辑
            console.warn('只能粘贴一列数据');
            return;
        } else {
            // 数字类型单元格可能会粘贴这种格式 "1.23 \r\n"
            while (pasteText.length && (pasteText[pasteText.length - 1] === '\n' || pasteText[pasteText.length - 1] === '\r')) {
                pasteText = pasteText.substr(0, pasteText.length - 1);
            }
            
            pasteTexts = pasteText.split('\r').map(e => typeof e === 'string' ? e.trim() : e);
        }
        
        if (pasteTexts.length > 1) {
            e.preventDefault();
            if (itemtype === 'refer') {
                // 避免触发参照编辑后
                this.pasting = true;
            } else if (itemtype === 'number') {
                for (let index = 0; index < pasteTexts.length; index++) {
                    let text = pasteTexts[index];
    
                    //todo校验字符串
                    if (typeof text === 'string') {
                        text = text.replace(/,/g, '');
                    }
                    // 当数值类型只输入一个'-'的时候就将它赋值成0
                    if (text === '-') {
                        text = 0;
                    }
                    let reg = /^(-?\d+)(\.\d+)?$/; //数字，正数，负数，0，小数
                    if (typeof text === 'string' && text.length < 500 && !reg.test(text)) {
                        toast({ 
                            color: 'danger', 
                            title: locale['table_tips'], 
                            content: locale['table_num_err_tips'],
                        });
                        return;
                    }
    
                    pasteTexts[index] = text;
                }
            }
            this.handleBatchChange(
                pasteTexts.map(text => ({
                    display: text,
                    value: text,
                })), 
                pasteTexts,
            );
        }
    }

    handleBatchChange = (pasteValue, texts) => {
        let { store, rowKey, colKey } = this.props,
            { cellSelectConfig } = store.getInstance().getComponent('Table').current.props,
            rows = store.getVisibleRows(),
            row = store.getRowData(rowKey),
            column = store.getColumnProps(colKey),
            oldValues = [],
            selectedRows = pasteValue.map((value, index) => {
                let currentRow = rows[row.viewIndex + index];
                if (currentRow) {
                    oldValues.push(currentRow.values[colKey]);
                    return {
                        ...currentRow,
                        values: {
                            ...currentRow.values,
                            [colKey]: value,
                        },
                    };
                } else {
                    oldValues.push({});
                    let rowKey = String(new Date().getTime()).slice(-5) + getSafeRandom().toString(12);
                    return {
                        key: rowKey,
                        rowid: rowKey,
                        rowId: rowKey,
                        status: ROW_STATUS.ADD,
                        values: { [colKey]: value },
                    };
                }
            });
        
            cellSelectConfig?.onChange(selectedRows, { column, row, oldValues, texts });
    }

    focus = () => {
        let cell = findDOMNode(this),
            item = cell?.querySelector('.template-item-wrapper');
        (item || cell).focus();
    }

    blur = () => {
        // 只有当前单元格激活，才能失焦，防止误伤
        if (findDOMNode(this)?.contains(document.activeElement)) {
            document.activeElement.blur();
        }
    }
}